import { Breakpoint, Button, ButtonProps } from '@mui/material';

import { Children, ReturnComponent } from '../../utils/reactTypes';
import { AxleDialog, AxleDialogActions, AxleDialogContent } from './AxleDialog';

export interface IAxleConfirmDialogProps {
    children: Children;
    swapButtonOrder?: boolean;
    cancelCta?: string;
    cancelButtonProps?: Omit<ButtonProps, 'onClick'>;
    close: () => void;
    confirmCta: string;
    confirmButtonEnabled?: boolean;
    confirmButtonProps?: Omit<ButtonProps, 'onClick'>;
    onConfirm: () => void;
    width?: Breakpoint;
    title?: string;
}

type AxleConfirmDialogContentProps = Omit<IAxleConfirmDialogProps, 'width' | 'title'> & {
    cancelCta: string;
    confirmButtonEnabled: boolean;
};

type AxleConfirmDialogButtonsProps = Omit<AxleConfirmDialogContentProps, 'children'>;

// The cancel and confirm pair, in the order the caller asked for.
export const AxleConfirmDialogButtons = ({
    cancelCta,
    cancelButtonProps = {},
    swapButtonOrder = false,
    close,
    confirmCta,
    confirmButtonEnabled,
    confirmButtonProps = {},
    onConfirm,
}: AxleConfirmDialogButtonsProps): ReturnComponent => {
    const cancelButton = (
        <Button
            key={'cancel'}
            fullWidth
            variant='outlined'
            color='primary'
            {...cancelButtonProps}
            onClick={close}
        >
            {cancelCta}
        </Button>
    );
    const confirmButton = (
        <Button
            key={'confirm'}
            fullWidth
            variant='outlined'
            color='error'
            disabled={!confirmButtonEnabled}
            {...confirmButtonProps}
            onClick={onConfirm}
        >
            {confirmCta}
        </Button>
    );

    return <>{swapButtonOrder ? [confirmButton, cancelButton] : [cancelButton, confirmButton]}</>;
};

export const AxleConfirmDialogContent = ({
    children,
    ...buttonProps
}: AxleConfirmDialogContentProps) => (
    <>
        <AxleDialogContent>{children}</AxleDialogContent>
        <AxleDialogActions>
            <AxleConfirmDialogButtons {...buttonProps} />
        </AxleDialogActions>
    </>
);

export const AxleConfirmDialog = ({
    isOpen,
    close,
    children,
    title = 'Confirm',
    confirmCta,
    cancelCta = 'Cancel',
    swapButtonOrder,
    cancelButtonProps,
    onConfirm,
    confirmButtonEnabled = true,
    confirmButtonProps,
    width = 'sm',
}: IAxleConfirmDialogProps & {
    isOpen: boolean;
    close: () => void;
}): ReturnComponent => {
    return (
        <AxleDialog
            dataTestId='confirm-dialog'
            isOpen={isOpen}
            close={close}
            title={title}
            width={width}
        >
            <AxleConfirmDialogContent
                swapButtonOrder={swapButtonOrder}
                cancelCta={cancelCta}
                cancelButtonProps={cancelButtonProps}
                close={close}
                confirmCta={confirmCta}
                confirmButtonEnabled={confirmButtonEnabled}
                confirmButtonProps={confirmButtonProps}
                onConfirm={onConfirm}
            >
                {children}
            </AxleConfirmDialogContent>
        </AxleDialog>
    );
};
